/**
 * Betriebstag-Detail (Spec 8.1) — die Kernmaske.
 *
 * Spiegelt das gewohnte Tagesblatt: Kassenblock oben, Personenblock unten,
 * Kontrollsumme sichtbar wie heute in K49/L49. Ohne diese Wiedererkennbarkeit
 * gibt es Akzeptanzprobleme.
 */
import { Fragment, useEffect, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  api,
  type BusinessDayDetail as DayDetail,
  type BusinessDaySummary,
  type CashSessionDto,
  type PoolDto,
  type WorkstationDayDto,
  type WorkstationDto,
} from "../lib/api";
import { dateTime, euro, hours as fmtHours, isZero, longDate, weekdayDate } from "../lib/format";
import { erklaereFehler } from "../lib/calcErrors";
import { SessionPanel } from "../components/SessionPanel";
import { ManualCashEntry } from "../components/ManualCashEntry";
import { GroupingSwitch } from "../components/GroupingSwitch";
import { TopfWahl } from "../components/TopfWahl";
import { SortKnopf, Suchfeld, ariaSort, useSortierung } from "../components/Sortierung";
import {
  passtZurSuche,
  vergleicheBetrag,
  vergleicheText,
  vergleicheZahl,
  type Richtung,
} from "../lib/sortierung";
import {
  Amount,
  Empty,
  ErrorBox,
  FlagChip,
  Info,
  PageHeader,
  Section,
  Spinner,
  Stat,
  StatusChip,
  isBlockingFlag,
} from "../components/ui";

export function BusinessDayDetail() {
  const { date = "" } = useParams();
  const [openWorkstation, setOpenWorkstation] = useState<string | null>(null);
  const [handeingabeOffen, setHandeingabeOffen] = useState(false);
  const queryClient = useQueryClient();

  // Was aufgeklappt war, gehört zum vorigen Tag.
  useEffect(() => {
    setOpenWorkstation(null);
    setHandeingabeOffen(false);
  }, [date]);

  // Beim Blättern bleibt der alte Tag stehen, bis der neue geladen ist: kein
  // Flackern über einen Ladekreis, und die Seite springt nicht nach oben.
  const detail = useQuery({
    queryKey: ["business-day", date],
    queryFn: () => api.get<DayDetail>(`/business-days/${date}`),
    retry: false,
    placeholderData: keepPreviousData,
  });

  const cash = useQuery({
    queryKey: ["business-day-cash", date],
    queryFn: () =>
      api.get<{ workstationDays: WorkstationDayDto[]; sessions: CashSessionDto[] }>(
        `/business-days/${date}/cash-sessions`,
      ),
    placeholderData: keepPreviousData,
  });

  const workstations = useQuery({
    queryKey: ["workstations"],
    queryFn: () => api.get<{ workstations: WorkstationDto[] }>("/workstations"),
  });

  const pools = useQuery({
    queryKey: ["pools"],
    queryFn: () => api.get<{ pools: PoolDto[] }>("/pools"),
  });

  const calculate = useMutation({
    mutationFn: () => api.post(`/business-days/${date}/calculate`),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  const navigate = useNavigate();
  const approve = useMutation({
    mutationFn: async () => {
      await api.post(`/business-days/${date}/approve`);
      // Danach gleich zum nächsten Tag derselben Monatshälfte, der noch nicht
      // freigegeben ist — so geht man eine Hälfte am Stück durch.
      const { days } = await api.get<{ days: BusinessDaySummary[] }>(
        `/business-days?from=${naechsterTag(date)}&to=${haelftenEnde(date)}`,
      );
      return days.find((d) => d.status === "DRAFT" || d.status === "CALCULATED")?.businessDate ?? null;
    },
    onSuccess: async (naechster) => {
      await queryClient.invalidateQueries();
      navigate(naechster ? `/betriebstage/${naechster}` : "/betriebstage");
      window.scrollTo({ top: 0 });
    },
  });

  if (detail.isLoading || cash.isLoading) return <Spinner label="Betriebstag wird geladen" />;
  if (detail.error) return <ErrorBox error={detail.error} />;

  const day = detail.data;
  // Noch der vorige Tag zu sehen: nichts daran ändern lassen.
  const wechselt = detail.isPlaceholderData || cash.isPlaceholderData;
  const workstationDays = cash.data?.workstationDays ?? [];
  const sessions = cash.data?.sessions ?? [];
  const wsById = new Map(workstations.data?.workstations.map((w) => [w.id, w]) ?? []);

  // Ausgeschlossene Kassen haben Status EXCLUDED und sind hier ohnehin nicht dabei.
  const openBlocking = sessions.filter((s) => s.status === "PROPOSED" && s.hasBlockingFlag);
  const balanced = day?.control?.isBalanced ?? false;
  const locked = day?.status === "LOCKED";
  // Altbestand aus dem Excel-Archiv: nur Person, Tag und Betrag, nie gerechnet.
  const isLegacy =
    day?.hasLegacyRows === true &&
    day.distributions.every((d) => d.source === "LEGACY_IMPORT");
  const canApprove =
    day?.status === "CALCULATED" && openBlocking.length === 0 && balanced && !locked;
  // Vor dem ersten Lauf gibt es keine Töpfe. Leere Summen und „Differenz ≠ 0"
  // sähen dann nach einem Fehler aus, obwohl schlicht noch nichts gerechnet ist.
  const nieGerechnet = !isLegacy && day?.calculatedAt === null;
  // Kassen, die eine Entscheidung brauchen — als Sprungziel in die eigene Tabelle.
  const blockierendeKassen = [
    ...new Map(
      openBlocking.map((s) => {
        const id = s.actsAsWorkstationId ?? s.workstationId;
        return [id, wsById.get(id)?.name ?? s.workstationName] as const;
      }),
    ),
  ];

  return (
    <>
      <PageHeader
        label="Betriebstag"
        title={longDate(date)}
        nav={<TagBlaettern nachbarn={wechselt ? null : (day?.nachbarn ?? null)} />}
        actions={
          <>
            <button
              className="btn-ghost"
              onClick={() => calculate.mutate()}
              disabled={calculate.isPending || locked || wechselt}
              title={
                locked
                  ? "Gesperrter Tag wird nicht neu gerechnet"
                  : "Töpfe und Verteilung neu berechnen"
              }
            >
              {calculate.isPending ? "Rechnet" : "Neu berechnen"}
            </button>
            <button
              className="btn-primary"
              onClick={() => approve.mutate()}
              disabled={!canApprove || approve.isPending || wechselt}
              title={
                locked
                  ? "Betriebstag ist gesperrt"
                  : canApprove
                  ? "Betriebstag freigeben"
                  : openBlocking.length > 0
                    ? openBlocking.length === 1
                      ? "Eine offene Kasse blockiert die Freigabe"
                      : `${openBlocking.length} offene Kassen blockieren die Freigabe`
                    : !balanced
                      ? "Kontrollsumme stimmt nicht"
                      : "Erst neu berechnen"
              }
            >
              Freigeben
            </button>
          </>
        }
      >
        {day && !isLegacy ? (
          <Veranstaltung key={date} date={date} titel={day.eventTitle} />
        ) : (
          (day?.eventTitle ?? null)
        )}
      </PageHeader>

      {calculate.error ? <ErrorBox error={calculate.error} /> : null}
      {approve.error ? <ErrorBox error={approve.error} /> : null}

      <div
        className={wechselt ? "pointer-events-none opacity-50 transition-opacity" : "transition-opacity"}
        aria-busy={wechselt}
      >
      {/* Kontrollsumme prominent, wie heute in K49/L49. */}
      <div className="mb-8 flex flex-wrap gap-6">
        {isLegacy ? (
          <Stat label="Summe Altbestand" value={euro(day?.distributionTotal ?? null)} />
        ) : nieGerechnet ? (
          <Stat
            label="Trinkgeld"
            value="noch nicht berechnet"
            info="Die Töpfe entstehen erst beim Berechnen. Vorher gibt es hier nichts zu vergleichen."
          />
        ) : (
          <>
            <Stat label="Summe Töpfe" value={euro(day?.control?.sumPools ?? null)} info="Alle Trinkgeldtöpfe des Tages zusammen." />
            {!isZero(day?.control?.sumUnallocated) ? (
              <Stat
                label="nicht verteilt"
                value={euro(day?.control?.sumUnallocated ?? null)}
                tone="alert"
                info="Trinkgeld aus einem Topf, in dem an diesem Tag niemand gearbeitet hat. Es wird nicht verteilt; der Grund steht unten."
              />
            ) : null}
            <Stat label="Summe verteilt" value={euro(day?.control?.sumDistributed ?? null)} info="Alle ausgeschütteten Beträge zusammen. Töpfe minus das, was nicht verteilt werden konnte." />
            <Stat
              label="Differenz"
              value={balanced ? "0,00 €" : "≠ 0"}
              tone={balanced ? "ok" : "alert"}
              info="Entspricht der Doppelprüfung im Tagesblatt: Töpfe minus Unverteilbares muss der Verteilung entsprechen. Bei einer Abweichung wird der Tag nicht freigegeben."
            />
            <Stat
              label="offen"
              value={String(openBlocking.length)}
              tone={openBlocking.length > 0 ? "alert" : "ok"}
              info="Kassensessions mit einem Hinweis, der die Freigabe blockiert."
            />
          </>
        )}
        {/* Getrennt oder gemeinsam: dort, wo man die Summen sieht, die sich ändern. */}
        {isLegacy || !day ? null : (
          <GroupingSwitch key={date} date={date} berechnung={day.berechnung} locked={locked} />
        )}
        <div className="border-l border-line pl-4">
          <div className="label mb-1">Status</div>
          <StatusChip status={day?.status ?? "DRAFT"} />
        </div>
      </div>

      {isLegacy ? (
        <div className="mb-8 rounded border border-line bg-surface px-4 py-3 text-sm text-muted">
          <strong className="text-ink">Altbestand aus dem Excel-Archiv.</strong> Übernommen
          wurden nur Person, Tag und Betrag. Kassen, Stunden und Arbeitsbereiche stehen im
          Archiv nicht. Der Tag ist gesperrt und wird nie neu gerechnet.
        </div>
      ) : null}

      {day?.nachSperre ? (
        <div className="mb-8 rounded border border-warn/40 bg-warn/5 px-4 py-3 text-sm">
          <strong className="text-warn">Nach der Sperre sind noch Daten eingegangen.</strong>{" "}
          {[
            day.nachSperre.stunden > 0
              ? `${day.nachSperre.stunden} ${day.nachSperre.stunden === 1 ? "Stundenzeile" : "Stundenzeilen"}`
              : null,
            day.nachSperre.kassen > 0
              ? `${day.nachSperre.kassen} ${day.nachSperre.kassen === 1 ? "Kassenbeleg" : "Kassenbelege"}`
              : null,
          ]
            .filter(Boolean)
            .join(" und ")}{" "}
          {day.nachSperre.stunden + day.nachSperre.kassen === 1 ? "kam" : "kamen"} nach dem{" "}
          {dateTime(day.nachSperre.seit)} dazu. Sie stecken nicht in der Auszahlung
          dieses Tages, der Tag wird nie neu gerechnet. Gehören sie dazu, kann die Administration
          den Tag mit Grund entsperren. War die Auszahlung schon überwiesen, muss die Differenz
          von Hand ausgeglichen werden.
        </div>
      ) : null}

      {!isLegacy && (openBlocking.length > 0 || nieGerechnet) ? (
        <div
          className={`mb-8 rounded border px-4 py-3 text-sm ${
            openBlocking.length > 0
              ? "border-alert/30 bg-alert/5"
              : "border-accent/30 bg-accent/5"
          }`}
        >
          {openBlocking.length > 0 ? (
            <>
              <strong className="text-alert">Nächster Schritt: {blockierendeKassen.length}{" "}
                {blockierendeKassen.length === 1 ? "Kasse" : "Kassen"} klären.</strong>{" "}
              Erst danach lässt sich der Tag berechnen und freigeben. Ein Klick springt zur
              Kasse und klappt die Erklärung auf.
              <div className="mt-2 flex flex-wrap gap-2">
                {blockierendeKassen.map(([id, name]) => (
                  <button
                    key={id}
                    className="btn-outline"
                    onClick={() => {
                      setOpenWorkstation(id);
                      document
                        .getElementById(`kasse-${id}`)
                        ?.scrollIntoView({ behavior: "smooth", block: "center" });
                    }}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <strong className="text-ink">Nächster Schritt: Tag berechnen.</strong> Alle
              Kassen sind geklärt. Das Berechnen bildet die Töpfe und verteilt sie auf die
              Stunden.
              <div className="mt-2">
                <button
                  className="btn-recommend"
                  onClick={() => calculate.mutate()}
                  disabled={calculate.isPending}
                >
                  {calculate.isPending ? "Rechnet" : "Jetzt berechnen"}
                </button>
              </div>
            </>
          )}
        </div>
      ) : null}

      {/* --- Kassenblock ------------------------------------------------- */}
      {isLegacy ? null : (
      <Section
        label="Kassen"
        title="Z-Bon, Kassenschnitt und Trinkgeld"
        actions={
          locked ? null : handeingabeOffen ? null : (
            <button className="btn-ghost" onClick={() => setHandeingabeOffen(true)}>
              Kasse von Hand erfassen
            </button>
          )
        }
      >
        {handeingabeOffen ? (
          <ManualCashEntry
            businessDate={date}
            workstations={workstations.data?.workstations ?? []}
            onDone={() => setHandeingabeOffen(false)}
            onCancel={() => setHandeingabeOffen(false)}
          />
        ) : null}
        {workstationDays.length === 0 ? (
          <Empty>Für diesen Tag liegen keine Kassenabschlüsse vor.</Empty>
        ) : (
          // Auf schmalen Bildschirmen seitlich scrollen statt abschneiden;
          // sonst wäre die Topf-Spalte dort nicht erreichbar.
          <div className="card overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Kasse</th>
                  <th className="w-32 text-right">
                    Z-Bon ecos
                    <Info text="Was die Kasse an Kartenumsatz gemeldet hat, aus den ecos-Kassenabschlüssen. Der Wert zählt seit dem vorherigen Abschluss mit, nicht seit Mitternacht." />
                  </th>
                  <th className="w-32 text-right">
                    Kassenschnitt VR
                    <Info text="Was tatsächlich am Karten-Terminal angekommen ist, aus dem VR-Export. Der Wert zählt seit der vorherigen Auslesung mit, nicht seit Mitternacht." />
                  </th>
                  <th className="w-28 text-right">
                    Trinkgeld
                    <Info text="Kassenschnitt minus Z-Bon, gerechnet je Kasse und Betriebstag. Einzelne Belege taugen dafür nicht, weil eine Terminal-Auslesung mehrere Kassenabschlüsse abdecken kann." />
                  </th>
                  <th className="w-40">
                    Topf
                    <Info text="In welchen Topf das Trinkgeld dieser Kasse fließt. Vorgabe aus den Stammdaten; eine Änderung gilt nur für diesen Tag, der Tag wird sofort neu gerechnet. Gelb umrandet heißt: weicht an diesem Tag von der Vorgabe ab." />
                  </th>
                  <th className="min-w-[15rem]">Hinweise</th>
                </tr>
              </thead>
              <tbody>
                {workstationDays.map((wd) => {
                  const own = sessions.filter(
                    (s) => (s.actsAsWorkstationId ?? s.workstationId) === wd.workstationId,
                  );
                  const open = openWorkstation === wd.workstationId;
                  // Sessions anderer Kassen, die heute unter dieser geführt werden.
                  // Ohne den Hinweis sähe die Zeile aus, als hätte diese Kasse den
                  // Umsatz selbst gemacht.
                  const verschoben = own.filter((s) => s.actsAsWorkstationId === wd.workstationId);
                  const excluded = wsById.get(wd.workstationId)?.excludedFromPool ?? false;
                  // Ausgeschlossene Kassen blockieren nie (Spec 2.3).
                  const blocking =
                    !excluded && own.some((s) => s.status === "PROPOSED" && s.hasBlockingFlag);
                  return (
                    <Fragment key={wd.workstationId}>
                      <tr
                        id={`kasse-${wd.workstationId}`}
                        className={`cursor-pointer ${blocking ? "bg-alert/5" : ""}`}
                        onClick={() => setOpenWorkstation(open ? null : wd.workstationId)}
                      >
                        <td>
                          {wsById.get(wd.workstationId)?.name ?? wd.workstationName}
                          <KassenKennung ws={wsById.get(wd.workstationId)} />
                          {verschoben.map((s) => (
                            <div key={s.id} className="mt-1 text-xs text-accent">
                              {s.workstationName}
                              {wsById.get(s.workstationId)?.posTerminalId
                                ? ` (Terminal ${wsById.get(s.workstationId)?.posTerminalId})`
                                : ""}{" "}
                              lief heute hier, Trinkgeld {euro(s.tipAmount)}
                              {s.topf ? ` in Topf ${s.topf.name}` : ""}
                            </div>
                          ))}
                          {own.length > 1 ? (
                            <span className="label ml-2 normal-case">
                              {own.length} Sessions
                            </span>
                          ) : null}
                        </td>
                        <td>
                          <Amount value={euro(wd.zBon)} muted={!wd.hasZBon} />
                        </td>
                        <td>
                          <Amount value={euro(wd.cardTotal)} muted={!wd.hasCardTotal} />
                        </td>
                        <td>
                          <Amount
                            value={excluded ? "–" : euro(wd.tipAmount)}
                            muted={excluded || isZero(wd.tipAmount)}
                          />
                        </td>
                        <td>
                          {excluded ? (
                            <span
                              className="text-xs text-muted"
                              title="Kasse ohne Trinkgeld, etwa eine Abendkasse. Sie steht in den Reports und wird deshalb angezeigt, fließt aber nie in einen Topf. Nur zur Kenntnis."
                            >
                              ohne Topf
                            </span>
                          ) : (
                            <TopfWahl
                              key={date}
                              date={date}
                              zeile={wd}
                              pools={pools.data?.pools ?? []}
                              gesperrt={locked}
                              offen={blocking}
                            />
                          )}
                        </td>
                        <td>
                          <div className="flex flex-wrap gap-1">
                            {(excluded ? [] : wd.flags).map((f) => (
                              <FlagChip key={f} flag={f} confirmed={!blocking} />
                            ))}
                          </div>
                        </td>
                      </tr>
                      {open
                        ? own.map((s) => (
                            <tr key={s.id}>
                              <td colSpan={6} className="border-t border-line bg-surface p-0">
                                <SessionPanel
                                  session={s}
                                  workstations={workstations.data?.workstations ?? []}
                                  gesperrt={locked}
                                />
                              </td>
                            </tr>
                          ))
                        : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>
      )}

      {/* --- Töpfe und Gruppierung --------------------------------------- */}
      {isLegacy ? null : (
      <Section label="Verteilung" title="Töpfe und Gruppen">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {(day?.pools ?? []).map((p) => {
            const group = day?.groups.find((g) => g.pools.some((x) => x.id === p.poolId));
            return (
              <div key={p.poolId} className="card p-4">
                <div className="label mb-1">{p.poolCode}</div>
                <div className="font-mono text-xl tabular-nums">{euro(p.tipAmount)}</div>
                <div className="mt-2 text-xs text-muted">
                  {fmtHours(p.totalHours)} · Umsatz {euro(p.grossRevenue)}
                </div>
                {group && group.pools.length > 1 ? (
                  <div className="mt-2 text-xs text-accent">
                    gemeinsam mit{" "}
                    {group.pools
                      .filter((x) => x.id !== p.poolId)
                      .map((x) => x.name)
                      .join(", ")}
                  </div>
                ) : null}
                {!isZero(p.unallocatedAmount) ? (
                  <div className="mt-2 text-xs text-alert">
                    {euro(p.unallocatedAmount)} ohne zugeordnete Stunden
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </Section>
      )}

      {/* --- Personenblock ----------------------------------------------- */}
      <VerteilungJePerson verteilung={day?.distributions ?? []} />

      {(day?.control?.warnings?.length ?? 0) > 0 ? (
        <Section label="Prüfung" title="Zur Kenntnis">
          <ul className="space-y-3 text-sm">
            {day?.control?.warnings?.map((w, i) => {
              const erklaert = erklaereFehler(w);
              return (
                <li key={i} className="rounded border border-line bg-surface px-4 py-3">
                  <p className="text-ink">{erklaert.was}</p>
                  {erklaert.wasTun ? (
                    <p className="mt-1 text-muted">{erklaert.wasTun}</p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </Section>
      ) : null}

      {(day?.control?.errors.length ?? 0) > 0 ? (
        <Section label="Prüfung" title="Warum der Tag noch nicht freigegeben werden kann">
          <ul className="space-y-3 text-sm">
            {day?.control?.errors.map((e, i) => {
              const erklaert = erklaereFehler(e);
              return (
                <li key={i} className="rounded border border-warn/30 bg-warn/5 px-4 py-3">
                  <p className="text-ink">{erklaert.was}</p>
                  {erklaert.wasTun ? (
                    <p className="mt-1 text-muted">{erklaert.wasTun}</p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </Section>
      ) : null}
      </div>
    </>
  );
}

export { isBlockingFlag };

/**
 * Zum vorigen und nächsten Betriebstag, ohne Umweg über die Liste — auch mit
 * den Pfeiltasten, solange kein Eingabefeld den Fokus hat. Übersprungen wird,
 * was keinen Betriebstag hat, etwa ein geschlossener Montag.
 */
function TagBlaettern({ nachbarn }: { nachbarn: { vorher: string | null; nachher: string | null } | null }) {
  const navigate = useNavigate();
  const vorher = nachbarn?.vorher ?? null;
  const nachher = nachbarn?.nachher ?? null;

  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.repeat || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (istEingabe(e.target)) return;
      const ziel = e.key === "ArrowLeft" ? vorher : e.key === "ArrowRight" ? nachher : null;
      if (!ziel) return;
      e.preventDefault();
      navigate(`/betriebstage/${ziel}`);
    };
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  }, [vorher, nachher, navigate]);

  return (
    <nav className="flex items-center gap-1" aria-label="Zwischen Betriebstagen blättern">
      <Blatt ziel={vorher} zurueck />
      <Blatt ziel={nachher} />
    </nav>
  );
}

function Blatt({ ziel, zurueck = false }: { ziel: string | null; zurueck?: boolean }) {
  const klasse = "btn-outline px-3 py-1.5";
  if (!ziel) {
    return (
      <span
        className={`${klasse} pointer-events-none opacity-40`}
        aria-disabled="true"
        title={zurueck ? "Kein früherer Betriebstag" : "Kein späterer Betriebstag"}
      >
        {zurueck ? "‹" : "›"}
      </span>
    );
  }
  return (
    <Link
      className={klasse}
      to={`/betriebstage/${ziel}`}
      title={`${zurueck ? "Voriger" : "Nächster"} Betriebstag: ${longDate(ziel)} (Taste ${zurueck ? "←" : "→"})`}
    >
      {zurueck ? `‹ ${weekdayDate(ziel)}` : `${weekdayDate(ziel)} ›`}
    </Link>
  );
}

/**
 * Verteilung je Person, wie im Tagesblatt nach PersNr — auf Wunsch nach
 * Name oder Höhe des Trinkgelds sortiert und nach Namen durchsuchbar
 * (Rückmeldung Oktober 2026).
 */
function VerteilungJePerson({ verteilung }: { verteilung: DayDetail["distributions"] }) {
  const [suche, setSuche] = useState("");
  const [sortierung, umschalten] = useSortierung<"persnr" | "name" | "stunden" | "trinkgeld">({
    spalte: "persnr",
    richtung: "auf",
  });
  const { spalte, richtung } = sortierung;
  const zeilen = verteilung
    .filter((d) => passtZurSuche(suche, d.name, d.personalNr, d.workArea))
    .sort((a, b) =>
      spalte === "name"
        ? vergleicheText(a.name, b.name, richtung)
        : spalte === "stunden"
          ? vergleicheBetrag(a.hours, b.hours, richtung)
          : spalte === "trinkgeld"
            ? vergleicheBetrag(a.amount, b.amount, richtung)
            : vergleicheZahl(a.personalNr, b.personalNr, richtung),
    );
  const kopf = (s: typeof spalte, text: string, ersteRichtung: Richtung = "auf") => (
    <SortKnopf aktiv={spalte === s} richtung={richtung} onClick={() => umschalten(s, ersteRichtung)}>
      {text}
    </SortKnopf>
  );

  return (
    <Section
      label="Personen"
      title={`Verteilung je Person (${verteilung.length})`}
      actions={
        verteilung.length > 0 ? (
          <Suchfeld wert={suche} onChange={setSuche} platzhalter="Name oder PersNr suchen" />
        ) : null
      }
    >
      {verteilung.length === 0 ? (
        <Empty>Noch nicht berechnet oder keine trinkgeldberechtigten Stunden.</Empty>
      ) : zeilen.length === 0 ? (
        <Empty>Niemand passt zu „{suche}“.</Empty>
      ) : (
        <div className="card overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                <th className="w-20" aria-sort={ariaSort(spalte === "persnr", richtung)}>
                  {kopf("persnr", "PersNr")}
                </th>
                <th aria-sort={ariaSort(spalte === "name", richtung)}>{kopf("name", "Name")}</th>
                <th className="w-40">Arbeitsbereich</th>
                <th className="w-24 text-right" aria-sort={ariaSort(spalte === "stunden", richtung)}>
                  {kopf("stunden", "Stunden", "ab")}
                  <Info text="Dauer brutto aus Gastromatic. Das ist die Verteilungsbasis." />
                </th>
                <th className="w-28 text-right">
                  vor Rundung
                  <Info text="Der rechnerische Anteil mit vier Nachkommastellen, bevor kaufmännisch gerundet wird. Macht jede Auszahlung nachprüfbar." />
                </th>
                <th className="w-28 text-right" aria-sort={ariaSort(spalte === "trinkgeld", richtung)}>
                  {kopf("trinkgeld", "Trinkgeld", "ab")}
                  <Info text="Gerundeter Anteil. Farbig hinterlegt, wenn diese Zeile den Rundungsausgleich der Gruppe trägt." />
                </th>
              </tr>
            </thead>
            <tbody>
              {zeilen.map((d) => (
                <tr key={d.id}>
                  <td className="font-mono text-xs text-muted">{d.personalNr}</td>
                  <td>
                    {d.name}
                    {d.isLocked ? (
                      <span className="chip-muted ml-2">festgesetzt</span>
                    ) : null}
                  </td>
                  <td className="text-muted">
                    {d.workArea ?? (
                      <span
                        className="chip-muted"
                        title="Altbestand aus dem Excel-Archiv: nur Person, Tag und Betrag"
                      >
                        Altbestand
                      </span>
                    )}
                  </td>
                  <td>
                    <Amount value={fmtHours(d.hours)} muted />
                  </td>
                  <td>
                    <Amount value={d.shareBeforeRounding ?? "–"} muted />
                  </td>
                  <td
                    className={
                      d.roundingAdjustment
                        ? d.roundingDelta?.startsWith("-")
                          ? "bg-alert/10"
                          : "bg-ok/10"
                        : ""
                    }
                    title={
                      d.roundingAdjustment
                        ? `Rundungsausgleich ${euro(d.roundingDelta)}`
                        : undefined
                    }
                  >
                    <Amount value={euro(d.amount)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-3 text-xs text-muted">
        Grün und rot markieren den Rundungsausgleich, wie heute im Tagesblatt. Die Differenz
        geht vollständig an den höchsten Einzelanteil der Gruppe.
      </p>
    </Section>
  );
}

/**
 * Die Veranstaltung des Abends, am Tag eingetragen. Sie entscheidet oft, wie
 * gerechnet wird — ein Konzert im CBE, Fußball im Biergarten — und stand
 * bisher nirgends, wo man sie beim Rechnen sieht.
 */
function Veranstaltung({ date, titel }: { date: string; titel: string | null }) {
  const queryClient = useQueryClient();
  const [offen, setOffen] = useState(false);
  const [text, setText] = useState(titel ?? "");
  const speichern = useMutation({
    mutationFn: (eventTitle: string) =>
      api.patch(`/business-days/${date}`, { eventTitle: eventTitle.trim() === "" ? null : eventTitle.trim() }),
    onSuccess: async () => {
      setOffen(false);
      await queryClient.invalidateQueries();
    },
  });

  if (!offen) {
    return (
      <>
        {titel ? <span className="text-ink">{titel}</span> : "Keine Veranstaltung eingetragen"}
        <button
          type="button"
          className="ml-3 text-xs text-accent hover:underline"
          onClick={() => {
            setText(titel ?? "");
            speichern.reset();
            setOffen(true);
          }}
        >
          {titel ? "ändern" : "Veranstaltung eintragen"}
        </button>
      </>
    );
  }
  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        speichern.mutate(text);
      }}
      onKeyDown={(e) => e.key === "Escape" && setOffen(false)}
    >
      <input
        className="field w-auto min-w-[18rem] flex-1 sm:max-w-md"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="z. B. CBE: Konzert · Yuca und Biergarten: Fußball"
        aria-label="Veranstaltung"
        maxLength={200}
        autoFocus
      />
      <button type="submit" className="btn-primary" disabled={speichern.isPending}>
        {speichern.isPending ? "Speichert" : "Speichern"}
      </button>
      <button type="button" className="btn-ghost" onClick={() => setOffen(false)}>
        Abbrechen
      </button>
      {speichern.error ? <span className="text-xs text-alert">{speichern.error.message}</span> : null}
    </form>
  );
}

/** In Feldern, Auswahlen und Texten gehören die Pfeiltasten dem Feld. */
function istEingabe(ziel: EventTarget | null): boolean {
  if (!(ziel instanceof HTMLElement)) return false;
  return ziel.isContentEditable || ["INPUT", "SELECT", "TEXTAREA"].includes(ziel.tagName);
}

/** Terminal-ID unter dem Kassennamen — damit man sie im Händler-Report findet. */
function KassenKennung({ ws }: { ws: WorkstationDto | undefined }) {
  if (!ws?.posTerminalId) return null;
  return (
    <div className="text-xs text-muted">
      Terminal <span className="font-mono">{ws.posTerminalId}</span>
    </div>
  );
}

function naechsterTag(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/** Letzter Tag der Abrechnungshälfte: der 15. oder der Monatsletzte. */
function haelftenEnde(iso: string): string {
  const [y, m, t] = iso.split("-").map(Number) as [number, number, number];
  if (t <= 15) return `${iso.slice(0, 8)}15`;
  const letzter = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${iso.slice(0, 8)}${String(letzter).padStart(2, "0")}`;
}
